import React, { useState } from 'react';
import { Container, Row, Col, Card, Table, Button, Badge, Alert, Spinner, Modal, Form, Pagination } from 'react-bootstrap';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { api } from '../../services/api';

interface Feedback {
  id: string;
  name: string;
  feedback: string;
  user: {
    id: string;
    name: string;
    email: string;
    role: string;
  };
  org: {
    id: string;
    name: string;
    slug: string;
  };
  createdAt: string;
}

export const FeedbacksPage: React.FC = () => {
  const queryClient = useQueryClient();
  const [selectedFeedback, setSelectedFeedback] = useState<Feedback | null>(null);
  const [showDetailModal, setShowDetailModal] = useState(false);
  const [showCreateModal, setShowCreateModal] = useState(false);
  const [showEditModal, setShowEditModal] = useState(false);
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [orgFilter, setOrgFilter] = useState<string>('');
  const [newFeedback, setNewFeedback] = useState({ name: '', feedback: '' });
  const [editFeedback, setEditFeedback] = useState({ id: '', name: '', feedback: '' });
  const [page, setPage] = useState(1);
  const itemsPerPage = 10;

  // Fetch feedbacks
  const { data: feedbacksData, isLoading, error } = useQuery({
    queryKey: ['feedbacks', orgFilter, page],
    queryFn: async () => {
      const params = new URLSearchParams();
      if (orgFilter) params.append('orgId', orgFilter);
      params.append('page', page.toString());
      params.append('limit', itemsPerPage.toString());
      const response = await api.get(`/feedback?${params.toString()}`);
      return response.data;
    },
  });

  // Fetch organizations for filter
  const { data: orgsData } = useQuery({
    queryKey: ['organizations'],
    queryFn: async () => {
      const response = await api.get('/admin/organizations');
      return response.data;
    },
  });

  const feedbacks: Feedback[] = feedbacksData?.data || [];
  const organizations = orgsData?.data || [];

  // Create feedback mutation
  const createFeedbackMutation = useMutation({
    mutationFn: async (data: { name: string; feedback: string }) => {
      const response = await api.post('/feedback', data);
      return response.data;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['feedbacks'] });
      setShowCreateModal(false);
      setNewFeedback({ name: '', feedback: '' });
    },
  });

  // Update feedback mutation
  const updateFeedbackMutation = useMutation({
    mutationFn: async (data: { id: string; name: string; feedback: string }) => {
      const response = await api.put(`/feedback/${data.id}`, {
        name: data.name,
        feedback: data.feedback,
      });
      return response.data;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['feedbacks'] });
      setShowEditModal(false);
      setEditFeedback({ id: '', name: '', feedback: '' });
    },
  });

  // Delete feedback mutation
  const deleteFeedbackMutation = useMutation({
    mutationFn: async (id: string) => {
      const response = await api.delete(`/feedback/${id}`);
      return response.data;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['feedbacks'] });
      setShowDeleteModal(false);
      setSelectedFeedback(null);
    },
  });

  const handleViewDetails = (feedback: Feedback) => {
    setSelectedFeedback(feedback);
    setShowDetailModal(true);
  };

  const handleCreateFeedback = async (e: React.FormEvent) => {
    e.preventDefault();
    if (newFeedback.name.trim() && newFeedback.feedback.trim()) {
      createFeedbackMutation.mutate(newFeedback);
    }
  };

  const handleEditClick = (feedback: Feedback) => {
    setEditFeedback({
      id: feedback.id,
      name: feedback.name,
      feedback: feedback.feedback,
    });
    setShowEditModal(true);
  };

  const handleUpdateFeedback = async (e: React.FormEvent) => {
    e.preventDefault();
    if (editFeedback.name.trim() && editFeedback.feedback.trim()) {
      updateFeedbackMutation.mutate(editFeedback);
    }
  };

  const handleDeleteClick = (feedback: Feedback) => {
    setSelectedFeedback(feedback);
    setShowDeleteModal(true);
  };

  const handleConfirmDelete = () => {
    if (selectedFeedback) {
      deleteFeedbackMutation.mutate(selectedFeedback.id);
    }
  };

  const formatDate = (dateString: string) => {
    const date = new Date(dateString);
    return date.toLocaleString('en-US', {
      year: 'numeric',
      month: 'short',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit',
    });
  };

  const getRoleBadge = (role: string) => {
    const roleColors: Record<string, string> = {
      ADMIN: 'danger',
      AGENT: 'warning',
      REQUESTER: 'info',
    };
    return <Badge bg={roleColors[role] || 'secondary'}>{role}</Badge>;
  };

  if (isLoading) {
    return (
      <Container fluid className="py-4 text-center">
        <Spinner animation="border" variant="primary" />
        <p className="mt-3">Loading feedbacks...</p>
      </Container>
    );
  }

  if (error) {
    return (
      <Container fluid className="py-3">
        <Alert variant="danger">
          <i className="fas fa-exclamation-circle me-2"></i>
          Error loading feedbacks. Please try again.
        </Alert>
      </Container>
    );
  }

  return (
    <Container fluid className="py-3">
      <Row className="mb-3">
        <Col>
          <div className="d-flex justify-content-between align-items-center">
            <div>
              <h2>
                <i className="fas fa-comment-dots me-2 text-primary"></i>
                User Feedbacks
              </h2>
              <p className="text-muted">View and manage feedback from all users</p>
            </div>
            <div className="d-flex align-items-center gap-3">
              <Badge bg="primary" pill style={{ fontSize: '1rem', padding: '0.5rem 1rem' }}>
                {feedbacks.length} Total
              </Badge>
              <Button 
                variant="primary" 
                onClick={() => setShowCreateModal(true)}
                className="d-flex align-items-center"
              >
                <i className="fas fa-plus me-2"></i>
                Create Feedback
              </Button>
            </div>
          </div>
        </Col>
      </Row>

      {/* Filter Row */}
      <Row className="mb-3">
        <Col md={4}>
          <Form.Group>
            <Form.Label className="small fw-semibold">Filter by Organization:</Form.Label>
            <Form.Select
              value={orgFilter}
              onChange={(e) => {
                setOrgFilter(e.target.value)
                setPage(1)
              }}
              size="sm"
            >
              <option value="">All Organizations</option>
              {organizations.map((org: any) => (
                <option key={org.id} value={org.id}>
                  {org.name}
                </option>
              ))}
            </Form.Select>
          </Form.Group>
        </Col>
      </Row>

      <Row>
        <Col>
          <Card className="shadow-sm">
            <Card.Body className="p-0">
              {feedbacks.length === 0 ? (
                <div className="text-center py-5">
                  <i className="fas fa-inbox fa-3x text-muted mb-3"></i>
                  <p className="text-muted">No feedbacks found</p>
                </div>
              ) : (
                <div className="table-responsive">
                  <Table hover className="mb-0">
                    <thead className="bg-light">
                      <tr>
                        <th>Name</th>
                        <th>Organization</th>
                        <th>Submitted By</th>
                        <th>Role</th>
                        <th>Feedback</th>
                        <th>Date</th>
                        <th>Actions</th>
                      </tr>
                    </thead>
                    <tbody>
                      {feedbacks.map((feedback) => (
                        <tr key={feedback.id}>
                          <td className="fw-semibold">{feedback.name}</td>
                          <td>
                            <Badge bg="info" className="text-dark">
                              {feedback.org.name}
                            </Badge>
                          </td>
                          <td>
                            <div className="small">
                              <div className="fw-semibold">{feedback.user.name}</div>
                              <div className="text-muted">{feedback.user.email}</div>
                            </div>
                          </td>
                          <td>{getRoleBadge(feedback.user.role)}</td>
                          <td>
                            <div
                              style={{
                                maxWidth: '300px',
                                overflow: 'hidden',
                                textOverflow: 'ellipsis',
                                whiteSpace: 'nowrap',
                              }}
                            >
                              {feedback.feedback}
                            </div>
                          </td>
                          <td className="text-muted small">{formatDate(feedback.createdAt)}</td>
                          <td>
                            <div className="d-flex gap-1">
                              <Button
                                variant="outline-primary"
                                size="sm"
                                onClick={() => handleViewDetails(feedback)}
                                title="View Details"
                              >
                                <i className="fas fa-eye"></i>
                              </Button>
                              <Button
                                variant="outline-warning"
                                size="sm"
                                onClick={() => handleEditClick(feedback)}
                                title="Edit Feedback"
                              >
                                <i className="fas fa-edit"></i>
                              </Button>
                              <Button
                                variant="outline-danger"
                                size="sm"
                                onClick={() => handleDeleteClick(feedback)}
                                title="Delete Feedback"
                              >
                                <i className="fas fa-trash"></i>
                              </Button>
                            </div>
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </Table>
                </div>
              )}
            </Card.Body>
          </Card>

          {/* Pagination */}
          {feedbacksData?.pagination?.pages > 1 && (
            <div className="d-flex justify-content-center mt-3">
              <Pagination>
                <Pagination.First onClick={() => setPage(1)} disabled={page === 1} />
                <Pagination.Prev onClick={() => setPage(page - 1)} disabled={page === 1} />
                
                {[...Array(feedbacksData.pagination.pages)].map((_, i) => {
                  const pageNum = i + 1
                  const totalPages = feedbacksData.pagination.pages
                  if (
                    pageNum === 1 ||
                    pageNum === totalPages ||
                    (pageNum >= page - 1 && pageNum <= page + 1)
                  ) {
                    return (
                      <Pagination.Item
                        key={pageNum}
                        active={pageNum === page}
                        onClick={() => setPage(pageNum)}
                      >
                        {pageNum}
                      </Pagination.Item>
                    )
                  } else if (pageNum === page - 2 || pageNum === page + 2) {
                    return <Pagination.Ellipsis key={pageNum} disabled />
                  }
                  return null
                })}
                
                <Pagination.Next onClick={() => setPage(page + 1)} disabled={page === feedbacksData.pagination.pages} />
                <Pagination.Last onClick={() => setPage(feedbacksData.pagination.pages)} disabled={page === feedbacksData.pagination.pages} />
              </Pagination>
            </div>
          )}
        </Col>
      </Row>

      {/* Detail Modal */}
      <Modal show={showDetailModal} onHide={() => setShowDetailModal(false)} size="lg">
        <Modal.Header closeButton>
          <Modal.Title>
            <i className="fas fa-comment-dots me-2"></i>
            Feedback Details
          </Modal.Title>
        </Modal.Header>
        <Modal.Body>
          {selectedFeedback && (
            <div>
              <Row className="mb-3">
                <Col md={6}>
                  <strong>Name:</strong>
                  <p>{selectedFeedback.name}</p>
                </Col>
                <Col md={6}>
                  <strong>Organization:</strong>
                  <p>
                    <Badge bg="info" className="text-dark">
                      {selectedFeedback.org.name}
                    </Badge>
                  </p>
                </Col>
              </Row>

              <Row className="mb-3">
                <Col md={6}>
                  <strong>Submitted By:</strong>
                  <p>
                    {selectedFeedback.user.name}
                    <br />
                    <span className="text-muted small">{selectedFeedback.user.email}</span>
                  </p>
                </Col>
                <Col md={6}>
                  <strong>Role:</strong>
                  <p>{getRoleBadge(selectedFeedback.user.role)}</p>
                </Col>
              </Row>

              <Row className="mb-3">
                <Col>
                  <strong>Date Submitted:</strong>
                  <p className="text-muted">{formatDate(selectedFeedback.createdAt)}</p>
                </Col>
              </Row>

              <Row>
                <Col>
                  <strong>Feedback:</strong>
                  <Card className="bg-light mt-2">
                    <Card.Body>
                      <p className="mb-0" style={{ whiteSpace: 'pre-wrap' }}>
                        {selectedFeedback.feedback}
                      </p>
                    </Card.Body>
                  </Card>
                </Col>
              </Row>
            </div>
          )}
        </Modal.Body>
        <Modal.Footer>
          <Button variant="secondary" onClick={() => setShowDetailModal(false)}>
            Close
          </Button>
        </Modal.Footer>
      </Modal>

      {/* Create Feedback Modal */}
      <Modal show={showCreateModal} onHide={() => setShowCreateModal(false)} size="lg">
        <Modal.Header closeButton>
          <Modal.Title>
            <i className="fas fa-plus-circle me-2"></i>
            Create New Feedback
          </Modal.Title>
        </Modal.Header>
        <Form onSubmit={handleCreateFeedback}>
          <Modal.Body>
            <Form.Group className="mb-3">
              <Form.Label>Name <span className="text-danger">*</span></Form.Label>
              <Form.Control
                type="text"
                placeholder="Enter name or title"
                value={newFeedback.name}
                onChange={(e) => setNewFeedback({ ...newFeedback, name: e.target.value })}
                required
              />
            </Form.Group>

            <Form.Group className="mb-3">
              <Form.Label>Feedback <span className="text-danger">*</span></Form.Label>
              <Form.Control
                as="textarea"
                rows={5}
                placeholder="Enter your feedback"
                value={newFeedback.feedback}
                onChange={(e) => setNewFeedback({ ...newFeedback, feedback: e.target.value })}
                required
              />
            </Form.Group>

            {createFeedbackMutation.isError && (
              <Alert variant="danger">
                <i className="fas fa-exclamation-circle me-2"></i>
                Failed to submit feedback. Please try again.
              </Alert>
            )}
          </Modal.Body>
          <Modal.Footer>
            <Button 
              variant="secondary" 
              onClick={() => setShowCreateModal(false)}
              disabled={createFeedbackMutation.isPending}
            >
              Cancel
            </Button>
            <Button 
              variant="primary" 
              type="submit"
              disabled={createFeedbackMutation.isPending || !newFeedback.name.trim() || !newFeedback.feedback.trim()}
            >
              {createFeedbackMutation.isPending ? (
                <>
                  <Spinner animation="border" size="sm" className="me-2" />
                  Submitting...
                </>
              ) : (
                <>
                  <i className="fas fa-check me-2"></i>
                  Submit Feedback
                </>
              )}
            </Button>
          </Modal.Footer>
        </Form>
      </Modal>

      {/* Edit Feedback Modal */}
      <Modal show={showEditModal} onHide={() => setShowEditModal(false)} size="lg">
        <Modal.Header closeButton>
          <Modal.Title>
            <i className="fas fa-edit me-2"></i>
            Edit Feedback
          </Modal.Title>
        </Modal.Header>
        <Form onSubmit={handleUpdateFeedback}>
          <Modal.Body>
            <Form.Group className="mb-3">
              <Form.Label>Name <span className="text-danger">*</span></Form.Label>
              <Form.Control
                type="text"
                placeholder="Enter name or title"
                value={editFeedback.name}
                onChange={(e) => setEditFeedback({ ...editFeedback, name: e.target.value })}
                required
              />
            </Form.Group>

            <Form.Group className="mb-3">
              <Form.Label>Feedback <span className="text-danger">*</span></Form.Label>
              <Form.Control
                as="textarea"
                rows={5}
                placeholder="Enter your feedback"
                value={editFeedback.feedback}
                onChange={(e) => setEditFeedback({ ...editFeedback, feedback: e.target.value })}
                required
              />
            </Form.Group>

            {updateFeedbackMutation.isError && (
              <Alert variant="danger">
                <i className="fas fa-exclamation-circle me-2"></i>
                Failed to update feedback. Please try again.
              </Alert>
            )}
          </Modal.Body>
          <Modal.Footer>
            <Button 
              variant="secondary" 
              onClick={() => setShowEditModal(false)}
              disabled={updateFeedbackMutation.isPending}
            >
              Cancel
            </Button>
            <Button 
              type="submit" 
              variant="warning"
              disabled={updateFeedbackMutation.isPending}
            >
              {updateFeedbackMutation.isPending ? (
                <>
                  <Spinner as="span" animation="border" size="sm" className="me-2" />
                  Updating...
                </>
              ) : (
                <>
                  <i className="fas fa-save me-2"></i>
                  Update Feedback
                </>
              )}
            </Button>
          </Modal.Footer>
        </Form>
      </Modal>

      {/* Delete Confirmation Modal */}
      <Modal show={showDeleteModal} onHide={() => setShowDeleteModal(false)}>
        <Modal.Header closeButton>
          <Modal.Title>
            <i className="fas fa-trash me-2 text-danger"></i>
            Confirm Delete
          </Modal.Title>
        </Modal.Header>
        <Modal.Body>
          <Alert variant="warning">
            <i className="fas fa-exclamation-triangle me-2"></i>
            Are you sure you want to delete this feedback?
          </Alert>
          {selectedFeedback && (
            <div>
              <p className="mb-2"><strong>Name:</strong> {selectedFeedback.name}</p>
              <p className="mb-0"><strong>Feedback:</strong> {selectedFeedback.feedback}</p>
            </div>
          )}
          {deleteFeedbackMutation.isError && (
            <Alert variant="danger" className="mt-3">
              <i className="fas fa-exclamation-circle me-2"></i>
              Failed to delete feedback. Please try again.
            </Alert>
          )}
        </Modal.Body>
        <Modal.Footer>
          <Button 
            variant="secondary" 
            onClick={() => setShowDeleteModal(false)}
            disabled={deleteFeedbackMutation.isPending}
          >
            Cancel
          </Button>
          <Button 
            variant="danger"
            onClick={handleConfirmDelete}
            disabled={deleteFeedbackMutation.isPending}
          >
            {deleteFeedbackMutation.isPending ? (
              <>
                <Spinner as="span" animation="border" size="sm" className="me-2" />
                Deleting...
              </>
            ) : (
              <>
                <i className="fas fa-trash me-2"></i>
                Delete Feedback
              </>
            )}
          </Button>
        </Modal.Footer>
      </Modal>
    </Container>
  );
};






